// FirewallRoutingTable.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, GitMerge,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Network, ArrowRight
} from "lucide-react";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";


// ── Types ─────────────────────────────────────────────────────────────────────
interface RoutingTableItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    cidr: string;
    ip_mask: string;
    type: string;
    gateway: string;
    next_hop: string;
    interface: string;
    distance: number;
    metric: number;
    priority: number;
}

// ── Type Badge ────────────────────────────────────────────────────────────────
const TYPE_STYLES: Record<string, string> = {
    static: "bg-blue-500/10 border-blue-500/20 text-blue-400",
    connect: "bg-green-500/10 border-green-500/20 text-green-400",
    ospf: "bg-orange-500/10 border-orange-500/20 text-orange-400",
    bgp: "bg-purple-500/10 border-purple-500/20 text-purple-400",
    rip: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
    isis: "bg-pink-500/10 border-pink-500/20 text-pink-400",
    kernel: "bg-gray-500/10 border-gray-500/20 text-gray-400",
};

const TypeBadge = ({ type }: { type: string }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase whitespace-nowrap ${TYPE_STYLES[type] ?? "bg-gray-500/10 border-gray-500/20 text-gray-400"}`}>
        {type}
    </span>
);

const isDefaultRoute = (cidr: string) => cidr === "0.0.0.0/0";
const isNoNextHop = (gw: string) => !gw || gw === "0.0.0.0";

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-blue-400" />
        : <ArrowDown size={10} className="text-blue-400" />;
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallRoutingTable({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<RoutingTableItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { cidr: { $ilike: `%${debouncedSearch}%` } },
                { gateway: { $ilike: `%${debouncedSearch}%` } },
                { interface: { $ilike: `%${debouncedSearch}%` } },
                { type: { $ilike: `%${debouncedSearch}%` } },
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit,
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_routing_table", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load routing table");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-sky-600/20 p-1.5 rounded-lg border border-sky-500/30">
                        <GitMerge size={16} className="text-sky-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Routing Table</h1>
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} routes
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-60 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 group-focus-within:text-sky-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search CIDR, gateway, type..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-sky-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-sky-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <Th label="Network (CIDR)" col="cidr" />
                                <Th label="Type" col="type" />
                                <Th label="Gateway" col="gateway" />
                                <Th label="Next Hop" col="next_hop" />
                                <Th label="Interface" col="interface" />
                                <Th label="Distance" col="distance" right />
                                <Th label="Metric" col="metric" right />
                                <Th label="Priority" col="priority" right />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading routing table...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <GitMerge size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">No routes found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => {
                                    const defRoute = isDefaultRoute(item.cidr);
                                    const noGw = isNoNextHop(item.gateway);

                                    return (
                                        <tr key={item.id} className="hover:bg-gray-700/30 transition-colors">

                                            {/* Network CIDR */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    {defRoute && (
                                                        <span className="px-1 py-0.5 text-[8px] font-black bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded uppercase shrink-0">
                                                            Default
                                                        </span>
                                                    )}
                                                    <span className={`font-mono text-[12px] font-bold ${defRoute ? "text-sky-300" : "text-cyan-300"}`}>
                                                        {item.cidr}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Type */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <TypeBadge type={item.type} />
                                            </td>

                                            {/* Gateway */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {noGw ? (
                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <ArrowRight size={10} className="text-gray-600 shrink-0" />
                                                        <span className="font-mono text-[11px] font-bold text-orange-300">{item.gateway}</span>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Next Hop */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {isNoNextHop(item.next_hop) ? (
                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                ) : (
                                                    <span className="font-mono text-[11px] text-amber-300/80">{item.next_hop}</span>
                                                )}
                                            </td>

                                            {/* Interface */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-gray-700/50 border-gray-600/40 text-gray-300">
                                                    <Network size={9} />
                                                    {item.interface}
                                                </span>
                                            </td>

                                            {/* Distance */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[11px] text-gray-200 mr-8">{item.distance}</span>
                                            </td>

                                            {/* Metric */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[11px] text-gray-200 mr-8">{item.metric}</span>
                                            </td>

                                            {/* Priority */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[11px] text-gray-200 mr-8">{item.priority}</span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-sky-400 hover:text-sky-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
